import axios from 'axios';
import {
    Button, Grid, Typography
} from 'components/atoms';
import { RE_PROCESS_TRANSACTION_BATCH } from 'const';
import { useCustomDropZone } from 'hooks';
import { useCallback } from 'react';
import { AiOutlineUpload } from 'react-icons/ai';
import { Auth, CustomError, Process } from 'types';
import { loadState } from 'utils';
import { getHttpErrorDetail } from 'utils/errorHandler';
import { rejectedFilesMessage, zoneStyle } from './ProcessUploadTemplate.const';
import { ProcessUploadTemplateProps } from './ProcessUploadTemplate.types';

/**
 * Este componente implementa el modal para cargar el archivo
 * que contiene los paquetes a reprocesar.
 * 
 * Se usa el customHook useDropZone para gestionar
 * la zona de arrastre de archivos del componente.
 */
const ProcessUploadTemplate = ({
    waitingResponse,
    setWaitingResponse,
    onClose,
    setOpen,
    setActionSnackBar,
    setErrorMessage,
    processId,
    process,
}: ProcessUploadTemplateProps) => {

    // URL para reprocesamiento en lote está definida en const/api.ts

    const {
        files,
        rejectedFiles,
        getRootProps,
        getInputProps,
        // onRemoveFiles, // No se usa pero está disponible si necesitamos limpiar archivos
        isDragActive,
    } = useCustomDropZone();

    const onUpload = useCallback(
        async () => {
            try {
                setWaitingResponse(true);
                if (!files.length) {
                    setWaitingResponse(false);
                    setErrorMessage('Debe cargar un archivo');
                    setOpen('warning');
                    return;
                }

                const auth = loadState<Auth>('auth');
                const username: string = auth?.user?.username || '';

                // Create form data to send the file
                const formData = new FormData();
                formData.append('file', files[0]);
                formData.append('user', username);

                // Usar el nombre del proceso si está disponible desde las props, de lo contrario buscar en localStorage
                let processName = process?.name;

                // Si no está disponible desde las props, intentar obtenerlo del localStorage
                if (!processName) {
                    const processFromStorage = loadState<Process>('currentProcess');
                    processName = processFromStorage?.code || processId;
                }

                formData.append('processName', processName);

                // Llamar al API para reprocesar mediante archivo Excel
                await axios.post(RE_PROCESS_TRANSACTION_BATCH, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                setWaitingResponse(false);
                onClose();
                setActionSnackBar('reprocess');
                setOpen('success');
            } catch (error) {
                setWaitingResponse(false);
                const errorDetail = getHttpErrorDetail(error as CustomError);
                setErrorMessage(errorDetail);
                setOpen('error');
            }
        },
        [files, onClose, processId, process, setActionSnackBar, setErrorMessage, setOpen, setWaitingResponse],
    );

    return (
        <>
            <Grid item xs={12} style={{ width: '100%', padding: '0 10px' }}>
                <div
                    style={zoneStyle}
                    {...getRootProps()}
                    className={`
            ${isDragActive ? 'border-3 border-success' : ''}
            ${rejectedFiles.length > 0 ? 'border-3 border-danger' : ''}
          `}
                >
                    <input {...getInputProps()} />
                    {files.length ? (
                        <>
                            <Grid container justify="center" spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="p" style={{ color: '#ffffff' }}>
                                        {`Archivo: ${files[0].name}`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="p2" style={{ color: '#a0aec0' }}>
                                        {`Tamaño: ${(files[0].size / 1024).toFixed(2)} KB`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid container justify="center" alignItems="center" spacing={0}>
                                <Grid item>
                                    <AiOutlineUpload
                                        size={24}
                                        style={{ color: '#a0aec0' }}
                                    />
                                </Grid>
                                <Grid item xs={12} style={{ textAlign: 'center', marginTop: 10 }}>
                                    <Typography variant="p" style={{ textAlign: 'center', color: '#ffffff', fontSize: '14px' }}>
                                        {isDragActive
                                            ? 'Suelta un archivo Excel aquí...'
                                            : 'Arrastra y suelta un archivo Excel aquí, o haz click para seleccionar archivos'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} style={{ textAlign: 'center' }}>
                                    <Typography variant="p2" style={{ textAlign: 'center', color: '#a0aec0', fontSize: '12px' }}>
                                        Solo archivos .xlsx, .xls
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </div>
            </Grid>
            {rejectedFiles.length > 0 && (
                <Grid item xs={12} style={{ padding: '10px', textAlign: 'center' }}>
                    <Typography variant="p" style={{ color: '#ff6b6b', fontSize: '13px' }}>
                        {rejectedFilesMessage(rejectedFiles[0].file.name)}
                    </Typography>
                </Grid>
            )}

            <Grid item xs={12} style={{ marginTop: '15px' }}>
                <Grid container spacing={2} justify="flex-end">
                    <Grid item>
                        <Button variant="contained" color="grey" onClick={onClose}>
                            Cancelar
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onUpload}
                            disabled={!files.length || waitingResponse}
                            loading={waitingResponse}
                        >
                            Cargar
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default ProcessUploadTemplate;